"use client";

import { useState, useEffect } from "react";
import {
  Shield,
  Award,
  Globe,
  CheckCircle,
  Clock,
  Users,
  FileText,
  Sparkles,
  Mountain,
  Palmtree,
  Building2,
  UserCheck,
  CreditCard
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useData } from "../context/DataContext";
import { useLanguage } from "../context/LanguageContext";
import api from "../api/api";
import Image from "next/image";
import BlogCard from "../components/BlogCard";


export default function HomePage() {
  const { currentUser } = useData();
  const { t } = useLanguage();
  const router = useRouter();

  const [visaTypes, setVisaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const DEFAULT_DESTINATION_ID = 101;
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/blogs", { params: { limit: 3 } })
      .then(res => setBlogs(res.data.data))
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    const fetchVisaTypes = async () => {
      try {
        const res = await api.get("/visa-types");
        setVisaTypes(res.data.data);
      } catch (err) {
        console.error("Error fetching visa types:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchVisaTypes();
  }, []);

  const ICONS = {
    Globe: Globe,
    Clock: Clock,
    Users: Users,
    Shield: Shield,
    Award: Award,
  };

  const getGridCols = (count) => {
    if (count === 1) return "grid-cols-1";
    if (count === 2) return "grid-cols-1 md:grid-cols-2";
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
  };

  const destinations = [
    {
    name: "Azerbaijan",
    description: [
      "Discover the “Land of Fire” with Baku’s futuristic skyline, medieval Old City, and serene Caspian Sea promenades.",
      "Enjoy city breaks, wine country tours, and Silk Route landscapes.",
    ],
  },
  {
    name: "India",
    description: [
      "Wander from the Himalayas to tropical coastlines, ancient forts, and buzzing bazaars in one unforgettable trip.",
      "Experience spiritual retreats, royal heritage stays, wildlife safaris, and world-famous festivals with effortless entry.",
    ],
  },
  {
    name: "Vietnam",
    description: [
      "Cruise karst-studded bays, explore lantern-lit ancient towns, and relax on pristine beaches across this dynamic Southeast Asian hotspot.",
      "Explore culture-rich cities, coffee highlands, and coastal escapes.",
    ],
  },
];

const offices = [
  {
    city: "Bangkok",
    country: "Thailand",
    image: "/images/offices/bangkok.jpg",
  },
  {
    city: "Nugegoda",
    country: "Sri Lanka",
    image: "/images/offices/srilanka.webp",
  },
  {
    city: "Seoul",
    country: "South Korea",
    image: "/images/offices/seoul.webp",
  },
  {
    city: "Sharjah",
    country: "UAE",
    image: "/images/offices/uae.webp",
  },
];



  return (
    <div className="overflow-hidden">
     <section className="relative w-full min-h-[70vh] md:h-screen text-white">
 
  {/* mobile view */}
  <Image
    src="/mobileView/banner-main.webp"
    alt="Explore your Destinations"
    fill
    priority
    className="object-cover object-top md:hidden"
  />
  {/* Desktop Image */}
  <Image
    src="/evisa-banner.webp"
    alt="Explore your Destinations"
    fill
    className="object-cover object-center hidden md:block"
    priority
  />

  {/* Overlay */}
  <div className="absolute inset-0 bg-black/40" />

  {/* Content Wrapper */}
  <div className="relative z-10 flex h-full items-center px-8 max-w-7xl mx-auto">


    {/* LEFT CONTENT */}
    <div className="w-full lg:w-1/2 mt-6">
      <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
        {t("Explore the World with Ease - Apply for Your eVisa with DU Global")}
      </h1>

      <p className="text-xl mb-4">
        {t("Azerbaijan • India • Vietnam")}
      </p>

      <p className="text-lg mb-6 text-gray-200">
        {t(
          "From iconic cultural landmarks to vibrant cities and scenic landscapes, DU Global simplifies your travel by handling your eVisa process seamlessly."
        )}
      </p>

      <button
        className="bg-red-800 hover:bg-red-700 font-semibold px-6 py-3 mb-6 rounded-full shadow-lg transition"
        onClick={() => router.push("/services")}
      >
        {t("Apply for Your eVisa")}
      </button>

      <p className="text-lg mb-6 text-gray-200">
        {t("Select your destination, choose your visa type, and apply online in minutes.")}
      </p>
    </div>

    {/* RIGHT FEATURES */}

     <div className="hidden lg:flex lg:w-1/2 flex-col justify-center pl-16 space-y-6">
        <div
          className="bg-white/10 backdrop-blur-md p-5 rounded-xl border border-white/20 hover:bg-white/20 transition"
        >
          <h3 className="text-lg font-semibold mb-5"><span className="text-white-400 text-2xl">✔</span> {t('Trusted by 1.7M+ travellers worldwide')}</h3>
          <h3 className="text-lg font-semibold mb-5"><span className="text-white-400 text-2xl">✔</span> {t('Multi-country eVisa expertise')}</h3>
          <h3 className="text-lg font-semibold mb-5"><span className="text-white-400 text-2xl">✔</span> {t('Secure, ISO-certified systems & global support')}</h3>
        </div>
    </div>
  </div>
</section>
<section className="py-16 bg-white">
  <div className="max-w-7xl mx-auto px-6">

    {/* Heading */}
    <div className="mb-10">
      <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-4xl text-center font-bold text-gray-900 mb-4 sm:mb-4 md:mb-8">
        {t("Our Global Offices")}
      </h2>
    </div>

    {/* Cards */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {offices.map((office, index) => (
        <div
          key={index}
          className="relative h-[260px] rounded-xl overflow-hidden group shadow-lg"
        >
          {/* Image */}
          <img
            src={office.image}
            alt={office.city}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

          {/* Content */}
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h3 className="text-lg font-semibold">
              {t(office.city)}
            </h3>
            <p className="text-sm opacity-90">
              {t(office.country)}
            </p>

            <a
              href="#footer"
              className="inline-block mt-3 text-sm font-medium text-yellow-400 hover:text-yellow-300"
            >
              {t("Locate Us")} →
            </a>
          </div>
        </div>
      ))}
    </div>

  </div>
</section>



{/* Introduction / Value Proposition */}
<section className="py-12 md:py-12 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto text-center">

      {/* Badge */}
      <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-4 md:mb-8">
        {t("A World of Incredible Diversity")}
      </h2>

      <p className="text-gray-700 font-light mb-2">
        {t("From royal palaces and desert dunes to Caspian skylines and emerald rice terraces, the world is full of journeys waiting to be lived.")}
      </p>
      <p className="text-gray-700 font-light">
        {t("With DU Global’s eVisa service your trip starts online: seamless applications, guided support, and peace of mind before you fly.")}
      </p>

      {/* Destinations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">

        {/* CARD */}
        {[
          {
            title: "AZERBAIJAN",
            icon: Building2,
            flag: "/flags/AZE.webp",
            gradient: "from-green-500 to-green-600",
            points: [
              "Discover the “Land of Fire” with Baku’s futuristic skyline, medieval Old City, and serene Caspian Sea promenades.",
              "Enjoy city breaks, wine country tours, and Silk Route landscapes.",
            ],
          },
          {
            title: "INDIA",
            icon: Mountain,
            flag: "/flags/IND.webp",
            gradient: "from-blue-500 to-blue-600",
            points: [
              "Wander from the Himalayas to tropical coastlines, ancient forts, and buzzing bazaars in one unforgettable trip.",
              "Experience spiritual retreats, royal heritage stays, wildlife safaris, and world-famous festivals with effortless entry.",
            ],
          },
          {
            title: "VIETNAM",
            icon: Palmtree,
            flag: "/flags/VNM.webp",
            gradient: "from-red-800 to-red-900",
            points: [
              "Cruise karst-studded bays, explore lantern-lit ancient towns, and relax on pristine beaches across this dynamic Southeast Asian hotspot.",
              "Explore culture-rich cities, coffee highlands, and coastal escapes.",
            ],
          },
        ].map((item, index) => (
          <div
            key={index}
            className="h-full bg-white p-4 sm:p-4 md:p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all flex flex-col"
          >
            {/* Icon */}
            {/* <div
              className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg`}
            >
              <item.icon className="h-8 w-8 text-white" />
            </div> */}

            {/* Title */}
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
            <img
              src={item.flag}
              alt={item.title}
              className="w-7 h-5 object-cover rounded-sm"
            />
            <span className="uppercase">{t(item.title)}</span>
          </h3>


            {/* Tick List */}
            <ul className="space-y-3 text-left flex-1">
              {item.points.map((point, i) => (
                <li key={i} className="flex items-start gap-3">

                  {/* Fixed outline circle */}
                  <span className="mt-[3px] flex h-5 w-5 min-w-[20px] items-center justify-center rounded-full border-2 border-green-600">
                    <svg
                      className="h-3 w-3 text-green-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>

                  {/* Text */}
                  <span className="text-gray-700 font-light leading-relaxed">
                    {t(point)}
                  </span>

                </li>
              ))}
            </ul>
            {/* CTA */}
            <a
              href={`/services?des=${encodeURIComponent(item.title)}`}
              className="mt-3 sm:mt-4 md:mt-6 mx-auto block w-[120px] text-center rounded-lg bg-gradient-to-r from-red-800 to-red-700 text-white text-sm font-semibold py-2 hover:shadow-md transition-all hover:scale-105"
            >
              {t("Apply Now")}
            </a>
          </div>
        ))}
      </div>
    </div>
  </div>
</section>



      {/* Application Process */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">

          {/* Badge */}
          <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-4xl font-bold text-gray-900 mb-8 sm:mb-10 md:mb-16">
            {t("Apply for Your Visa in 3 Easy Steps")}
          </h2>

          {/* Steps */}
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 items-stretch">

            {/* Connector Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-1 bg-gradient-to-r from-red-200 via-red-400 to-green-400 z-0" />

            {/* Step 1 */}
            <div className="relative z-10 group h-full">
              <div className="bg-white rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 h-full flex flex-col">

                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-600 text-white flex items-center justify-center text-lg font-bold">
                  1
                </div>

                <UserCheck className="h-8 w-8 text-red-600 mx-auto mb-4 group-hover:scale-110 transition" />
                <div className="flex-1 flex flex-col text-center">
                  <h3 className="text-xl font-bold mb-2">{t("Register")}</h3>
                  <p className="text-gray-600 font-light">
                    {t("Create your account to start your visa application")}
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative z-10 group h-full">
              <div className="bg-white rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 h-full flex flex-col">

                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-orange-600 text-white flex items-center justify-center text-lg font-bold">
                  2
                </div>

                <FileText className="h-8 w-8 text-orange-600 mx-auto mb-4 group-hover:scale-110 transition" />
                 <div className="flex-1 flex flex-col text-center">
                  <h3 className="text-xl font-bold mb-2">{t("Upload Documents")}</h3>
                  <p className="text-gray-600 font-light">
                    {t("Fill the visa application form online")}
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative z-10 group h-full">
              <div className="bg-white rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 h-full flex flex-col">

                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-600 text-white flex items-center justify-center text-lg font-bold">
                  3
                </div>

                <CreditCard className="h-8 w-8 text-green-600 mx-auto mb-4 group-hover:scale-110 transition" />
               <div className="flex-1 flex flex-col text-center">
                <h3 className="text-xl font-bold mb-2">{t("Make Payment")}</h3>
                <p className="text-gray-600 font-light">
                  {t("Pay securely using our online payment gateway")}
                </p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            {/* <div className="relative z-10 group h-full">
              <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 h-full flex flex-col">

                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-600 text-white flex items-center justify-center text-lg font-bold">
                  4
                </div>

                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-4 group-hover:scale-110 transition" />
                <div className="flex-1 flex flex-col text-center">
                <h3 className="text-xl font-bold mb-2">{t("Verify & Additional Details")}</h3>
                <p className="text-gray-600 font-light">
                  {t("Check and complete the additional details for your eVisa")}
                </p>
                </div>
              </div>
            </div> */}

            {/* Step 5 */}
            {/* <div className="relative z-10 group h-full">
              <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 h-full flex flex-col">

                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-600 text-white flex items-center justify-center text-lg font-bold">
                  5
                </div>

                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-4 group-hover:scale-110 transition" />
                <div className="flex-1 flex flex-col text-center">
                <h3 className="text-xl font-bold mb-2">{t("We Take It From Here")}</h3>
                <p className="text-gray-600 font-light">
                  {t("We receive and process your application. Track updates anytime from your dashboard.")}
                </p>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Quick Overview of Visa Types */}
      {/* <section className="py-20 md:py-15 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-5 py-2 mb-6">
              <Globe className="h-5 w-5 text-gray-700" />
              <span className="text-sm font-semibold text-gray-700 tracking-wide">{t("MULTIPLE OPTIONS AVAILABLE")}</span>
            </div>
            <h2 className="text-4xl md:text-4xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
              {t("Visa Types — At a Glance")}
            </h2>
            <p className="font-medium text-gray-700 max-w-3xl mx-auto font-light">
              {t("A brief snapshot of major visa types offered")}
            </p>
          </div>

         {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-800 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-500 text-lg font-light">Loading visa types...</p>
          </div>
        ) : (
          <div className={`grid ${getGridCols(visaTypes.length)} gap-6`}>
            {visaTypes.slice(0, 4).map((visa,index) => {
             // Get the actual component from the ICONS map
              return (
              <div
                key={index}
                className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200"
              >
                <div className="p-6">
                  <div className={`${visa.color} w-14 h-14 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300 group-hover:scale-110 transform`}>
                    <Globe className="w-7 h-7 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {visa.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {visa.description}
                  </p>

                  <button className="text-sm font-semibold text-gray-700 hover:text-gray-900 flex items-center group-hover:gap-2 gap-1 transition-all duration-300">
                    Learn More
                    <span className="text-lg">→</span>
                  </button>
                </div>
                <div className={`h-1 ${visa.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
              </div>
            );
            })}
          </div>
        )}
          {visaTypes.length > 4 && (
            <div className="text-center mt-12">
              <button
                onClick={() => router.push("/services")}
                className="px-10 py-4 bg-red-800 text-white rounded-xl text-lg font-bold hover:bg-red-900 transition-all inline-flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                {t("View All Visa Types")}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </section> */}

      {/* Why Choose DU Global */}
      <section className="py-12 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-6 leading-tight tracking-tight">
              {t("Why Choose DU Global")}
            </h2>
            <p className="font-medium text-gray-700 max-w-3xl mx-auto font-light">
              {t("Your trusted partner for hassle-free visa processing")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Expertise & Global Footprint */}
            <div className="bg-white p-5 sm:p-5 md:p-10 rounded-2xl border-2 border-gray-200 hover:border-red-800 hover:shadow-xl transition-all">
              <div className="bg-gradient-to-br from-red-800 to-red-900 w-12 h-12 sm:w-12 sm:h-12 md:w-16 md:h-16  rounded-xl flex items-center justify-center mb-3 sm:mb-3 md:mb-6 shadow-lg">
                <Globe className="h-6 w-6 sm:h-6 sm:w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2 sm:mb-2 md:mb-5 text-gray-900">{t("GLOBAL EXPERTISE")}</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("Presence in 6 countries")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("1.7M+ Visas processed worldwide")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("Trusted by millions of travellers")}</span>
                </li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="bg-white p-5 sm:p-5 md:p-10 rounded-2xl border-2 border-gray-200 hover:border-red-800 hover:shadow-xl transition-all">
              <div className="bg-gradient-to-br from-red-800 to-red-900 w-12 h-12 sm:w-12 sm:h-12 md:w-16 md:h-16  rounded-xl flex items-center justify-center mb-3 sm:mb-3 md:mb-6 shadow-lg">
                <Shield className="h-6 w-6 sm:h-6 sm:w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2 sm:mb-2 md:mb-5 text-gray-900">{t("Secure & Compliant")}</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("ISO-certified information security")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("ISO/IEC 27001 & ISO/IEC 27701 certified")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("Bank-level data protection")}</span>
                </li>
              </ul>
            </div>

            {/* Support & Processing */}
            <div className="bg-white p-5 sm:p-5 md:p-10 rounded-2xl border-2 border-gray-200 hover:border-red-800 hover:shadow-xl transition-all">
              <div className="bg-gradient-to-br from-red-800 to-red-900 w-12 h-12 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-xl flex items-center justify-center mb-3 sm:mb-3 md:mb-6 shadow-lg">
                <Users className="h-6 w-6 sm:h-6 sm:w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2 sm:mb-2 md:mb-5 text-gray-900">{t("Dedicated Support")}</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("Smooth processing from application to visa grant")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("Fast processing within 48-72 hours")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-base font-light">{t("Efficient customer support")}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Blog Section */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">

          <div className="text-center mb-6 sm:mb-10 md:mb-16">
            <h2 className="text-3xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 md:mb-6 leading-tight tracking-tight">
              Latest Blogs
            </h2>
            <p className="font-medium text-gray-700 max-w-3xl mx-auto font-light">
              Read our Blogs to get the inspiration you need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogs.map(blog => (
              <BlogCard key={blog.id} blog={blog} />
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => router.push("/blogs")}
              className="bg-red-800 text-white px-6 py-2 rounded-lg"
            >
              Read All Blogs →
            </button>
          </div>

        </div>
      </section>
    </div>
  );
}